/**
 * The vocabulary shared by the MusicXML reader and writer.
 *
 * Both directions need the same three things: what a tick is worth in the file's own
 * units, what a part is called there, and how the handful of enumerations line up with
 * the document's. Keeping them here is what stops the two sides drifting, which is the
 * failure mode that makes a round trip quietly lossy rather than loudly broken.
 */

import { PPQ, ticksOf } from "../document/duration";
import type { PartId } from "../document/ids";
import { capacityOf } from "../document/query";
import type { BarlineStyle, Score } from "../document/types";

/** How a bar's closing style is spelled in MusicXML, and back. */
export const BAR_STYLES: Readonly<Record<BarlineStyle, string | null>> = {
  regular: null,
  double: "light-light",
  final: "light-heavy",
  dashed: "dashed",
  heavy: "heavy",
  none: "none",
};

/** The reverse of {@link BAR_STYLES}, with everything unrecognised landing on `regular`. */
export function barlineStyleOf(raw: string | null): BarlineStyle {
  for (const [style, spelling] of Object.entries(BAR_STYLES) as [BarlineStyle, string | null][]) {
    if (spelling !== null && spelling === raw) return style;
  }
  return "regular";
}

/** How many semitones an accidental name is worth, for reading `<accidental>`. */
export const ACCIDENTAL_ALTER: Readonly<Record<string, number>> = {
  "double-flat": -2,
  "flat-flat": -2,
  flat: -1,
  natural: 0,
  sharp: 1,
  "double-sharp": 2,
  sharp_sharp: 2,
};

function gcd(a: number, b: number): number {
  let x = Math.abs(Math.round(a));
  let y = Math.abs(Math.round(b));
  while (y !== 0) [x, y] = [y, x % y];
  return x;
}

/**
 * The smallest `<divisions>` that writes every duration in this score as a whole number.
 *
 * The document counts in 6720 ticks to the quarter so that any note it can write is exact,
 * but exporting at that resolution puts a five-digit number on every note for no reason.
 * Dividing out the common factor of everything the score actually uses -- note values, bar
 * lengths, tempo units and the beats chord symbols stand on -- gives the same durations in
 * the smallest integers -- usually 1, 2 or 4 for ordinary music, larger only
 * when the piece really does contain a tuplet that needs it.
 */
export function divisionsFor(score: Score): number {
  let common = PPQ;
  const fold = (ticks: number): void => {
    if (ticks > 0) common = gcd(common, ticks);
  };

  for (let index = 0; index < score.measures.length; index += 1) {
    fold(capacityOf(score, index));
    const tempo = score.measures[index]?.tempo;
    if (tempo !== null && tempo !== undefined) fold(ticksOf(tempo.unit));
  }
  for (const part of score.parts) {
    for (const measure of Object.values(part.measures)) {
      for (const voice of measure.voices) {
        for (const event of voice.events) fold(ticksOf(event.duration));
      }
      // A chord symbol stands at a tick of its own, which may be finer than anything
      // written under it: a bar of whole rests with a chord on the third beat needs
      // divisions that can say "third beat" even though no note there does.
      for (const harmony of measure.harmonies) fold(harmony.onset);
      // And a figure does the same under the bass: a stack changing halfway through a
      // held note needs divisions fine enough to say where it changes.
      for (const stack of measure.figures) fold(stack.onset);
    }
  }

  return Math.max(1, PPQ / common);
}

/** Ticks as the file's own duration units. */
export function toDivisions(ticks: number, divisions: number): number {
  return Math.round((ticks * divisions) / PPQ);
}

/** The file's duration units back as ticks. */
export function fromDivisions(value: number, divisions: number): number {
  return divisions <= 0 ? 0 : Math.round((value * PPQ) / divisions);
}

/** `P1`, `P2`, ... in score order: MusicXML names parts by a document-wide id, we do not. */
export function partNames(score: Score): ReadonlyMap<PartId, string> {
  const names = new Map<PartId, string>();
  score.parts.forEach((part, index) => names.set(part.id, `P${index + 1}`));
  return names;
}

/** Quarter notes per minute, which is the only tempo `<sound>` understands. */
export function soundTempo(bpm: number, unitTicks: number): number {
  return Math.round(bpm * (unitTicks / PPQ) * 1000) / 1000;
}
